import axios from 'axios'
import { signInWithCustomToken, signOut } from 'firebase/auth'
import $http from '../../utils/http'
import router from '../../router'
import i18n from '../../i18n/index'
import devicePush from '../../utils/devicePush'
import webPush from '../../utils/webPush'
import parseJwtPayload from '../../utils/jwt'

// Кэш idToken для Cordova: нативный getCurrentUser делает принудительный refresh
// при каждом вызове, поэтому без кэша каждый API-запрос ходит в securetoken.googleapis.com.
// Кэш дублируется в localStorage: при холодном старте ещё живой токен (~1ч) используется
// сразу, без обращения к Firebase — иначе каждый рестарт приложения упирается в сеть/троттлинг
const ID_TOKEN_CACHE_KEY = 'rekassa.kz-auth-id-token-cache'

let cachedIdToken = null
let idTokenCacheHydrated = false

const loadPersistedIdToken = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ID_TOKEN_CACHE_KEY))
    if (parsed && parsed.token && parsed.uid && parsed.exp > Date.now() / 1000) {
      return parsed
    }
  } catch (e) { /* повреждённый кэш или недоступный localStorage — просто идём к плагину */ }
  return null
}

const storeIdTokenCache = (value) => {
  cachedIdToken = value
  try {
    if (value) {
      localStorage.setItem(ID_TOKEN_CACHE_KEY, JSON.stringify(value))
    } else {
      localStorage.removeItem(ID_TOKEN_CACHE_KEY)
    }
  } catch (e) { /* localStorage недоступен — работаем на кэше в памяти */ }
}

// Телеметрия потери сессии (веб): флаг «сессия была» живёт в localStorage и
// переживает потерю Firebase-хранилища. Ставит sessionWatcher, снимает наш
// signOut ДО firebase signOut - так watcher отличает свой выход от внезапного
export const HAD_SESSION_KEY = 'rekassa.kz-auth-had-session'

const parseJwtExp = (token) => parseJwtPayload(token)?.exp || 0

// Мемоизация slackId импersonate-сессии, чтобы не декодировать JWT на каждый запрос
let lastImpersonateToken = null
let lastImpersonateSlackId = null

const getImpersonateSlackId = (token) => {
  if (!token) {
    return null
  }
  if (token !== lastImpersonateToken) {
    const payload = parseJwtPayload(token)
    // В ID token developer-claims лежат на верхнем уровне; в custom token - под claims
    lastImpersonateSlackId = payload?.impersonate?.slackId || payload?.claims?.impersonate?.slackId || null
    lastImpersonateToken = token
  }
  return lastImpersonateSlackId
}

const auth = {
  namespaced: true,

  state: {
    user: null,
    firebaseAuth: null,
  },

  getters: {
  },

  mutations: {
    set(state, { type, value }) {
      state[type] = value
    },
  },

  actions: {
    setFirebaseAuth({ commit }, payload) {
      commit('set', { type: 'firebaseAuth', value: payload })
    },

    async getBearerToken({ state, dispatch }, options) {
      const forceRefresh = !!(options && options.forceRefresh)
      let bearerToken = null
      let currentUser = null
      let userUid = null

      // Cordova
      if (window.cordova !== undefined) {
        // Холодный старт: поднимаем ещё живой токен из localStorage
        if (!idTokenCacheHydrated) {
          idTokenCacheHydrated = true
          if (!cachedIdToken) {
            cachedIdToken = loadPersistedIdToken()
          }
        }

        // Отдаём кэшированный токен, пока до истечения больше 5 минут
        const nowSeconds = Date.now() / 1000
        if (!forceRefresh && cachedIdToken && cachedIdToken.exp - nowSeconds > 300) {
          return { bearerToken: cachedIdToken.token, userUid: cachedIdToken.uid, impersonateSlackId: getImpersonateSlackId(cachedIdToken.token) }
        }

        currentUser = await new Promise(resolve => {
          window.FirebasePlugin.getCurrentUser((user) => resolve(user), () => resolve(null))
        })
        if (currentUser && currentUser.idToken) {
          bearerToken = currentUser.idToken
          userUid = currentUser.uid
          storeIdTokenCache({ token: bearerToken, uid: userUid, exp: parseJwtExp(bearerToken) })
        } else if (!forceRefresh && cachedIdToken && cachedIdToken.exp > nowSeconds) {
          // Нативный refresh не удался (сеть/троттлинг) — используем ещё живой кэш
          bearerToken = cachedIdToken.token
          userUid = cachedIdToken.uid
        }
      // Web
      } else {
        currentUser = state.firebaseAuth.currentUser
        if (currentUser) {
          try {
            bearerToken = await currentUser.getIdToken(forceRefresh)
          } catch (error) {
            // Телеметрия расхода СМС: почему веб не смог обновить ID-токен
            dispatch('analytics/logEvent', { eventName: 're_auth_token_refresh_failed', eventProperties: { code: error?.code || String(error) } }, { root: true })
            throw error
          }
          userUid = currentUser.uid
        }
      }

      return { bearerToken, userUid, impersonateSlackId: getImpersonateSlackId(bearerToken) }
    },

    sendOtp({ }, payload) {
      return new Promise((resolve, reject) => {
        axios.post(`${import.meta.env.VITE_API_ROOT_URL}/sms/v2/send-otp`, payload.data).then((result) => {
          resolve(result.data)
        }).catch((error) => {
          reject(error)
        })
      })
    },

    verifyOtp({ }, payload) {
      return new Promise((resolve, reject) => {
        axios.post(`${import.meta.env.VITE_API_ROOT_URL}/sms/v2/verify-otp`, payload.data).then((result) => {
          resolve(result.data)
        }).catch((error) => {
          reject(error)
        })
      })
    },

    async setUser({ commit, dispatch, state }, user) {
      if (user) {
        commit('set', { type: 'user', value: (await $http.get('/api/auth/me')).data })
        // First ever login
        if (state.user.registrationDate === null && state.user.phone) {
          dispatch('analytics/logEvent', { eventName: 're_user_first_registered', eventProperties: { phone: state.user.phone } }, { root: true })
        }
      }
    },

    setName({ commit, dispatch }, payload) {
      return new Promise((resolve, reject) => {
        $http.post(`/api/auth/me/set-name?name=${payload.name}`).then((result) => {
          commit('set', { type: 'user', value: result.data })
          resolve()
        }).catch((error) => {
          dispatch('tools/showSnackbar', { message: `${i18n.t('произошла_ошибка')}:  ${error.message}` }, { root: true })
          reject()
        })
      })
    },

    setReferrer({ commit, dispatch }, payload) {
      return new Promise((resolve, reject) => {
        $http.post(`/api/auth/me/set-referrer?referrer=${payload.referrer}`).then((result) => {
          commit('set', { type: 'user', value: result.data })
          resolve()
        }).catch((error) => {
          dispatch('tools/showSnackbar', { message: `${i18n.t('произошла_ошибка')}:  ${error.message}` }, { root: true })
          reject()
        })
      })
    },

    setAgreementSeen({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        $http.post('/api/auth/me/set-agreement-seen').then((result) => {
          commit('set', { type: 'user', value: result.data })
          resolve()
        }).catch((error) => {
          dispatch('tools/showSnackbar', { message: `${i18n.t('произошла_ошибка')}:  ${error.message}` }, { root: true })
          reject()
        })
      })
    },

    signInWithCustomToken({ state }, payload) {
      storeIdTokenCache(null)
      // Cordova
      if (window.cordova !== undefined) {
        return new Promise((resolve, reject) => {
          window.FirebasePlugin.signInUserWithCustomToken(payload.token, () => {
            window.FirebasePlugin.getCurrentUser((user) => {
              resolve({ user })
            }, () => {
              reject()
            })
          }, (error) => {
            reject(error)
          })
        })
      }
      // Web
      return signInWithCustomToken(state.firebaseAuth, payload.token)
    },

    // Есть ли активная firebase-сессия (в т.ч. на холодном старте, когда state.user ещё null)
    hasActiveSession({ state }) {
      // Cordova
      if (window.cordova !== undefined) {
        return new Promise((resolve) => {
          window.FirebasePlugin.getCurrentUser((user) => resolve(!!user), () => resolve(false))
        })
      }
      // Web
      return state.firebaseAuth.authStateReady().then(() => !!state.firebaseAuth.currentUser)
    },

    // redirect: false - выйти без перехода на /signin (используется при входе по
    // кастомному токену, чтобы не сбросить ?token= из URL)
    signOut({ state, commit, dispatch }, payload) {
      const redirect = payload?.redirect !== false
      storeIdTokenCache(null)
      // Свой выход: снимаем флаг до firebase signOut, чтобы sessionWatcher
      // не посчитал его внезапной потерей сессии
      localStorage.removeItem(HAD_SESSION_KEY)

      const finalize = () => {
        console.debug('User signed out')
        if (redirect) {
          router.push('/signin').catch(() => {})
        }
        dispatch('positions/clear', null, { root: true })
        dispatch('tools/setAppInitialized', false, { root: true })
        commit('set', { type: 'user', value: null })
      }

      // Сначала отписка пушей: DELETE авторизуется токеном ещё активного пользователя
      // Cordova
      if (window.cordova !== undefined) {
        return devicePush.unsubscribe().then(() => new Promise((resolve) => {
          window.FirebasePlugin.signOutUser(() => {
            finalize()
            resolve()
          }, () => {
            finalize()
            resolve()
          })
        }))
      }
      // Web
      return webPush.unsubscribe().then(() => signOut(state.firebaseAuth).then(finalize).catch(finalize))
    },
  },
}

export default auth
